import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Copy, CheckCircle2, Trash2, X, Save, Edit2, Power, Sparkles, Upload, FileImage, Loader2, Printer, ScrollText, Layers, Wand2 } from "lucide-react";

const Menus = () => {
    const navigate = useNavigate();
    const [menus, setMenus] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showNew, setShowNew] = useState(false);
    const [editing, setEditing] = useState(null);
    const [duplicating, setDuplicating] = useState(null);
    const [duplicateName, setDuplicateName] = useState("");
    const [aiImport, setAiImport] = useState(null); // {menu}
    const [showMulti, setShowMulti] = useState(false);
    const [multiSel, setMultiSel] = useState([]);
    const [multiBusy, setMultiBusy] = useState(false);

    const load = async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/menus");
            setMenus(data);
        } catch (e) { toast.error(formatError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const activate = async (m) => {
        try {
            await api.post(`/menus/${m.id}/activate`);
            toast.success(`Menu "${m.name}" attivato`);
            load();
        } catch (e) { toast.error(formatError(e)); }
    };

    const openMultiActivate = () => {
        const currentActive = menus.filter((m) => m.active).map((m) => m.id);
        setMultiSel(currentActive.length > 0 ? currentActive : (menus[0] ? [menus[0].id] : []));
        setShowMulti(true);
    };

    const toggleMulti = (id) => {
        setMultiSel((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    };

    const confirmMultiActivate = async () => {
        if (multiSel.length === 0) {
            toast.error("Seleziona almeno un menu");
            return;
        }
        setMultiBusy(true);
        try {
            const { data } = await api.post("/menus/activate-multi", { menu_ids: multiSel });
            toast.success(`${data.activated_count} menu attivati: ${data.menus.map((m) => m.name).join(" + ")}`);
            setShowMulti(false);
            await load();
        } catch (e) { toast.error(formatError(e)); }
        setMultiBusy(false);
    };

    const reclassifyDepartments = async () => {
        if (!window.confirm("Riclassificare tutti i piatti del menu in base al nome (pizza→pizzeria, vino/birra/caffè→bar)?\n\nL'operazione è sicura e idempotente.")) return;
        try {
            const { data } = await api.post("/menu/reclassify-departments");
            toast.success(
                `Riclassificati ${data.items_updated}/${data.items_scanned} piatti · ` +
                `Cucina: ${data.by_department.cucina} · Bar: ${data.by_department.bar} · Pizzeria: ${data.by_department.pizzeria}`,
                { duration: 6000 }
            );
            await load();
        } catch (e) { toast.error(formatError(e)); }
    };

    const remove = async (m) => {
        if (m.is_default) { toast.error("Il menu predefinito non può essere eliminato"); return; }
        if (!window.confirm(`Eliminare definitivamente il menu "${m.name}" e tutti i suoi piatti?`)) return;
        try {
            await api.delete(`/menus/${m.id}`);
            toast.success("Menu eliminato");
            load();
        } catch (e) { toast.error(formatError(e)); }
    };

    const duplicate = async () => {
        try {
            const res = await api.post(`/menus/${duplicating.id}/duplicate`, { name: duplicateName });
            toast.success(`Menu duplicato (${res.data.categories} categorie, ${res.data.items} piatti)`);
            await load();
            setDuplicating(null);
            setDuplicateName("");
        } catch (e) { toast.error(formatError(e)); }
    };

    const printPdf = async (m) => {
        try {
            const res = await api.get(`/menus/${m.id}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const w = window.open(url, "_blank");
            if (w) {
                w.addEventListener("load", () => {
                    try { w.focus(); w.print(); } catch (e) { /* no-op */ }
                });
            }
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (e) { toast.error(formatError(e)); }
    };

    const toDaily = async (m) => {
        try {
            const { data } = await api.post(`/menus/${m.id}/to-daily`);
            if (data.created) {
                toast.success(`"${m.name}" copiato in Menu del Giorno (${data.items} piatti)`);
            } else {
                toast(`Trovato Menu del Giorno esistente per "${m.name}"`, { description: `${data.items} piatti già presenti` });
            }
            navigate("/menu-del-giorno");
        } catch (e) { toast.error(formatError(e)); }
    };

    return (
        <div className="space-y-6" data-testid="menus-page">
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-brand mb-2">Carta · Multi-Menu</div>
                    <h1 className="font-display text-4xl font-bold">Gestione Menu</h1>
                    <p className="text-elegant text-sm mt-2">
                        Crea menu separati per Pranzo, Cena, Eventi.
                        Puoi attivare <strong className="text-brand">UNO O PIÙ menu contemporaneamente</strong> (es. &quot;Lunedì Pranzo&quot; + &quot;Pizza&quot; + &quot;Bevande&quot; → menu unico).
                        Il coperto del menu primario è quello usato in cassa.
                    </p>
                </div>
                <div className="flex gap-2">
                    <button onClick={reclassifyDepartments} data-testid="reclassify-button"
                        title="Riclassifica automaticamente tutti i piatti in cucina/bar/pizzeria"
                        className="h-12 px-4 border-2 border-purple-500/60 text-purple-300 hover:bg-purple-900/20 font-bold rounded-md flex items-center gap-2 transition-colors uppercase tracking-wider text-xs">
                        <Wand2 className="w-4 h-4" /> Riclassifica reparti
                    </button>
                    <button onClick={openMultiActivate} data-testid="multi-activate-button"
                        className="h-12 px-4 bg-amber-500 hover:bg-amber-400 text-deep font-bold rounded-md flex items-center gap-2 transition-colors">
                        <Layers className="w-4 h-4" /> Combina Menu
                    </button>
                    <button onClick={() => setShowNew(true)} data-testid="add-menu-button"
                        className="h-12 px-5 bg-brand text-deep font-bold rounded-md flex items-center gap-2">
                        <Plus className="w-4 h-4" /> Nuovo Menu
                    </button>
                </div>
            </div>

            {/* Banner riassuntivo menu attivi */}
            {(() => {
                const activeMenus = menus.filter((m) => m.active);
                if (activeMenus.length === 0) return null;
                return (
                    <div className="bg-brand/10 border border-brand/40 rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="active-menus-banner">
                        <div className="flex items-center gap-2 flex-wrap">
                            <CheckCircle2 className="w-4 h-4 text-brand flex-shrink-0" />
                            <span className="text-xs uppercase tracking-wider text-brand font-bold">
                                {activeMenus.length === 1 ? "Menu attivo:" : `Menu combinati attivi (${activeMenus.length}):`}
                            </span>
                            <div className="flex gap-1 flex-wrap">
                                {activeMenus.map((m) => (
                                    <span key={m.id} className="text-xs px-2 py-1 rounded bg-brand/20 text-brand border border-brand/40 font-medium">
                                        {m.name}
                                    </span>
                                ))}
                            </div>
                        </div>
                        {activeMenus.length > 1 && (
                            <button onClick={openMultiActivate} data-testid="manage-combo"
                                className="text-[10px] uppercase tracking-wider text-brand hover:text-warm font-bold">
                                Modifica combinazione
                            </button>
                        )}
                    </div>
                );
            })()}

            {loading ? (
                <div className="text-elegant">Caricamento...</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {menus.map((m) => (
                        <div key={m.id}
                            data-testid={`menu-card-${m.id}`}
                            className={`bg-surface border rounded-lg p-5 transition-all ${
                                m.active ? "border-brand shadow-[0_0_0_1px_rgba(39,182,139,0.2)]" : "border-[#1f2c44]"
                            }`}>
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="font-display font-bold text-xl flex-1">{m.name}</div>
                                {m.active && (
                                    <span className="text-[10px] uppercase tracking-wider bg-brand/15 text-brand px-2 py-1 rounded flex items-center gap-1">
                                        <CheckCircle2 className="w-3 h-3" /> Attivo
                                    </span>
                                )}
                                {m.is_default && !m.active && (
                                    <span className="text-[10px] uppercase tracking-wider bg-amber-900/30 text-amber-400 px-2 py-1 rounded">
                                        Predefinito
                                    </span>
                                )}
                            </div>
                            <div className="text-xs text-elegant min-h-[2.5rem] mb-3">
                                {m.description || "Nessuna descrizione"}
                            </div>
                            <div className="text-xs text-elegant flex items-center gap-2 pb-3 border-b border-[#1f2c44]">
                                <span>Coperto:</span>
                                {m.coperto_enabled ? (
                                    <span className="text-brand font-bold">€ {(m.coperto_amount || 0).toFixed(2)}</span>
                                ) : (
                                    <span className="text-elegant">Disabilitato</span>
                                )}
                            </div>
                            <div className="flex flex-wrap gap-2 pt-3">
                                {!m.active && (
                                    <button onClick={() => activate(m)} data-testid={`activate-menu-${m.id}`}
                                        className="flex-1 h-10 bg-brand/10 hover:bg-brand/20 border border-brand text-brand rounded-md text-xs uppercase tracking-wider flex items-center justify-center gap-1">
                                        <Power className="w-3 h-3" /> Attiva
                                    </button>
                                )}
                                <button onClick={() => setEditing(m)} data-testid={`edit-menu-${m.id}`}
                                    className="h-10 w-10 border border-[#1f2c44] hover:border-brand rounded-md flex items-center justify-center" title="Modifica">
                                    <Edit2 className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => setAiImport({ menu: m })} data-testid={`ai-import-menu-${m.id}`}
                                    className="h-10 w-10 border border-[#1f2c44] hover:border-brand rounded-md flex items-center justify-center" title="Importa da PDF/Foto con AI">
                                    <Sparkles className="w-3.5 h-3.5 text-brand" />
                                </button>
                                <button onClick={() => toDaily(m)} data-testid={`to-daily-${m.id}`}
                                    className="h-10 px-3 border border-brand/40 hover:border-brand text-brand hover:bg-brand/5 rounded-md flex items-center gap-1.5 text-xs uppercase tracking-wider" title="Porta su Menu del Giorno (per stampa PDF)">
                                    <ScrollText className="w-3.5 h-3.5" /> Su Menu Giorno
                                </button>
                                <button onClick={() => printPdf(m)} data-testid={`print-menu-${m.id}`}
                                    className="h-10 w-10 border border-brand/40 hover:border-brand text-brand rounded-md flex items-center justify-center" title="Stampa PDF elegante">
                                    <Printer className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => { setDuplicating(m); setDuplicateName(`${m.name} (copia)`); }}
                                    data-testid={`duplicate-menu-${m.id}`}
                                    className="h-10 w-10 border border-[#1f2c44] hover:border-brand rounded-md flex items-center justify-center" title="Duplica">
                                    <Copy className="w-3.5 h-3.5" />
                                </button>
                                {!m.is_default && (
                                    <button onClick={() => remove(m)} data-testid={`delete-menu-${m.id}`}
                                        className="h-10 w-10 border border-red-700/40 text-red-400 hover:bg-red-900/20 rounded-md flex items-center justify-center" title="Elimina">
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {(showNew || editing) && (
                <MenuForm
                    menu={editing}
                    onClose={() => { setShowNew(false); setEditing(null); }}
                    onSaved={() => { setShowNew(false); setEditing(null); load(); }}
                />
            )}

            {duplicating && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <div className="bg-surface border border-[#1f2c44] rounded-lg p-6 max-w-md w-full">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="font-display text-xl font-bold">Duplica menu</h2>
                            <button onClick={() => setDuplicating(null)}><X /></button>
                        </div>
                        <div className="text-sm text-elegant mb-3">
                            Verrà creata una copia di <strong className="text-warm">{duplicating.name}</strong> con tutte le categorie e i piatti.
                        </div>
                        <input
                            type="text"
                            value={duplicateName}
                            onChange={(e) => setDuplicateName(e.target.value)}
                            placeholder="Nome del nuovo menu"
                            data-testid="duplicate-menu-name-input"
                            className="w-full h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none"
                        />
                        <div className="flex gap-2 mt-4">
                            <button onClick={() => setDuplicating(null)} className="flex-1 h-12 border border-[#1f2c44] rounded-md">
                                Annulla
                            </button>
                            <button onClick={duplicate} disabled={!duplicateName.trim()}
                                data-testid="confirm-duplicate-button"
                                className="flex-1 h-12 bg-brand text-deep font-bold rounded-md disabled:opacity-50">
                                Duplica
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {aiImport && (
                <AiImportMenuModal
                    menu={aiImport.menu}
                    onClose={() => setAiImport(null)}
                    onDone={() => { setAiImport(null); load(); }}
                />
            )}

            {/* Modale: Combina più menu */}
            {showMulti && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => !multiBusy && setShowMulti(false)}>
                    <div onClick={(e) => e.stopPropagation()} className="bg-surface border-2 border-amber-500/60 rounded-2xl p-6 max-w-xl w-full max-h-[85vh] overflow-y-auto" data-testid="multi-modal">
                        <div className="flex items-center gap-2 mb-2">
                            <Layers className="w-5 h-5 text-amber-400" />
                            <div className="text-xs uppercase tracking-[0.3em] text-amber-300">Combina menu</div>
                        </div>
                        <div className="font-display text-2xl font-bold mb-1">Quali menu vuoi rendere disponibili?</div>
                        <div className="text-xs text-elegant mb-4">Seleziona uno o più menu. I piatti di tutti i menu selezionati appariranno insieme nel terminale cameriere, sul portale clienti e sulle stampe. Es: <em>Lunedì Pranzo + Pizza + Bevande = menu unico</em>.</div>

                        <div className="space-y-2 mb-4">
                            {menus.map((m) => {
                                const checked = multiSel.includes(m.id);
                                return (
                                    <label key={m.id}
                                        data-testid={`multi-row-${m.id}`}
                                        className={`flex items-start gap-3 p-3 border-2 rounded-md cursor-pointer transition-colors ${
                                            checked ? "border-amber-500/70 bg-amber-900/15" : "border-[#1f2c44] hover:border-amber-500/40 bg-deep"
                                        }`}>
                                        <input type="checkbox" checked={checked} onChange={() => toggleMulti(m.id)}
                                            data-testid={`multi-check-${m.id}`}
                                            className="mt-1 w-5 h-5 accent-amber-500" />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-bold text-warm">{m.name}</span>
                                                {m.active && <span className="text-[9px] uppercase tracking-wider bg-brand/20 text-brand px-1.5 py-0.5 rounded">Attivo ora</span>}
                                                {m.is_default && <span className="text-[9px] uppercase tracking-wider bg-amber-900/30 text-amber-400 px-1.5 py-0.5 rounded">Predefinito</span>}
                                            </div>
                                            <div className="text-[10px] text-elegant mt-0.5">
                                                {m.description || "—"}
                                                {m.coperto_enabled && <span className="ml-2 text-brand">· Coperto € {(m.coperto_amount || 0).toFixed(2)}</span>}
                                            </div>
                                        </div>
                                    </label>
                                );
                            })}
                        </div>

                        <div className="bg-deep border border-[#1f2c44] rounded p-3 mb-4 text-xs">
                            <span className="text-elegant">Selezionati: </span>
                            <span className="font-bold text-amber-300">{multiSel.length} menu</span>
                            {multiSel.length > 0 && (
                                <span className="text-elegant ml-1">— {menus.filter((m) => multiSel.includes(m.id)).map((m) => m.name).join(" + ")}</span>
                            )}
                        </div>

                        <div className="flex gap-2">
                            <button onClick={() => setShowMulti(false)} disabled={multiBusy} data-testid="multi-cancel"
                                className="flex-1 h-12 border border-[#1f2c44] rounded text-elegant uppercase tracking-wider text-xs font-bold disabled:opacity-50">
                                Annulla
                            </button>
                            <button onClick={confirmMultiActivate} disabled={multiBusy || multiSel.length === 0}
                                data-testid="multi-confirm"
                                className="flex-[1.5] h-12 bg-amber-500 hover:bg-amber-400 disabled:bg-amber-900/30 disabled:cursor-not-allowed text-deep font-bold rounded uppercase tracking-wider text-xs flex items-center justify-center gap-2 transition-colors">
                                {multiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                                {multiBusy ? "Attivo..." : "Attiva combinazione"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const MenuForm = ({ menu, onClose, onSaved }) => {
    const [form, setForm] = useState(menu || {
        name: "",
        description: "",
        coperto_enabled: false,
        coperto_amount: 2.0,
        coperto_child_enabled: false,
        coperto_child_amount: 1.0,
        active: false,
    });

    const save = async () => {
        try {
            const payload = {
                name: form.name,
                description: form.description,
                coperto_enabled: form.coperto_enabled,
                coperto_amount: parseFloat(form.coperto_amount) || 0,
                coperto_child_enabled: form.coperto_child_enabled,
                coperto_child_amount: parseFloat(form.coperto_child_amount) || 0,
            };
            if (menu) {
                await api.patch(`/menus/${menu.id}`, payload);
                toast.success("Menu aggiornato");
            } else {
                await api.post("/menus", { ...payload, active: false });
                toast.success("Menu creato");
            }
            onSaved();
        } catch (e) { toast.error(formatError(e)); }
    };

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-surface border border-[#1f2c44] rounded-lg p-6 max-w-lg w-full">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="font-display text-2xl font-bold">{menu ? "Modifica" : "Nuovo"} Menu</h2>
                    <button onClick={onClose}><X /></button>
                </div>
                <div className="space-y-3">
                    <div>
                        <label className="text-xs uppercase tracking-wider text-elegant mb-1 block">Nome</label>
                        <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="Es. Pranzo, Cena, Menu Eventi"
                            data-testid="menu-name-input"
                            className="w-full h-12 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wider text-elegant mb-1 block">Descrizione</label>
                        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                            placeholder="Note interne (es. Pranzo lavoro 12:30-15:00)"
                            data-testid="menu-description-input"
                            className="w-full h-20 p-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none" />
                    </div>

                    <div className="bg-surface-elevated border border-[#1f2c44] rounded-md p-4">
                        <label className="flex items-center gap-3 cursor-pointer">
                            <input type="checkbox" checked={!!form.coperto_enabled}
                                onChange={(e) => setForm({ ...form, coperto_enabled: e.target.checked })}
                                data-testid="menu-coperto-toggle"
                                className="w-4 h-4 accent-brand" />
                            <span className="text-sm font-medium">Coperto attivo per questo menu</span>
                        </label>
                        {form.coperto_enabled && (
                            <>
                                <div className="mt-3">
                                    <label className="text-xs uppercase tracking-wider text-elegant mb-1 block">
                                        Importo coperto adulto a persona (€)
                                    </label>
                                    <input type="number" min="0" step="0.50"
                                        value={form.coperto_amount}
                                        onChange={(e) => setForm({ ...form, coperto_amount: e.target.value })}
                                        data-testid="menu-coperto-amount-input"
                                        className="w-full h-12 px-3 bg-deep border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none" />
                                </div>
                                <div className="mt-4 pt-4 border-t border-[#1f2c44]">
                                    <label className="flex items-center gap-3 cursor-pointer">
                                        <input type="checkbox" checked={!!form.coperto_child_enabled}
                                            onChange={(e) => setForm({ ...form, coperto_child_enabled: e.target.checked })}
                                            data-testid="menu-coperto-child-toggle"
                                            className="w-4 h-4 accent-brand" />
                                        <span className="text-sm font-medium">Coperto ridotto per bambini</span>
                                    </label>
                                    {form.coperto_child_enabled && (
                                        <div className="mt-3">
                                            <label className="text-xs uppercase tracking-wider text-elegant mb-1 block">
                                                Importo coperto bambino a persona (€)
                                            </label>
                                            <input type="number" min="0" step="0.50"
                                                value={form.coperto_child_amount}
                                                onChange={(e) => setForm({ ...form, coperto_child_amount: e.target.value })}
                                                data-testid="menu-coperto-child-amount-input"
                                                className="w-full h-12 px-3 bg-deep border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none" />
                                            <div className="text-[10px] text-elegant mt-1">Lasciare a 0 per bambini gratis</div>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
                <button onClick={save} disabled={!form.name?.trim()}
                    data-testid="save-menu-button"
                    className="w-full h-12 mt-5 bg-brand text-deep font-bold uppercase tracking-wider rounded-md flex items-center justify-center gap-2 disabled:opacity-50">
                    <Save className="w-4 h-4" /> {menu ? "Aggiorna" : "Crea"}
                </button>
            </div>
        </div>
    );
};

export default Menus;

const AiImportMenuModal = ({ menu, onClose, onDone }) => {
    const fileRef = useRef(null);
    const [file, setFile] = useState(null);
    const [parsing, setParsing] = useState(false);
    const [committing, setCommitting] = useState(false);
    const [preview, setPreview] = useState(null); // {categories: [...], stats}
    const [mode, setMode] = useState("append"); // append | replace

    const onPick = (f) => {
        if (!f) return;
        const validTypes = ["application/pdf", "image/jpeg", "image/jpg", "image/png", "image/webp"];
        const okExt = /\.(pdf|jpe?g|png|webp)$/i.test(f.name);
        if (!validTypes.includes(f.type) && !okExt) {
            toast.error("Carica un file PDF, JPG, PNG o WebP");
            return;
        }
        if (f.size > 15 * 1024 * 1024) {
            toast.error("File troppo grande (max 15 MB)");
            return;
        }
        setFile(f);
    };

    const parse = async () => {
        if (!file) return;
        setParsing(true);
        setPreview(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(`/menus/${menu.id}/import-ai-preview`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 120000, // 2 min
            });
            setPreview(data);
            toast.success(`Estratti ${data.stats.items} piatti in ${data.stats.categories} categorie`);
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setParsing(false);
        }
    };

    const commit = async () => {
        if (!preview) return;
        setCommitting(true);
        try {
            const payload = {
                categories: preview.categories,
                replace: mode === "replace",
            };
            await api.post(`/menus/${menu.id}/import-ai-commit`, payload);
            toast.success(`Menu "${menu.name}" aggiornato (${preview.stats.items} piatti)`);
            onDone();
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setCommitting(false);
        }
    };

    const removeItem = (ci, ii) => {
        setPreview((p) => {
            const cats = [...p.categories];
            cats[ci] = { ...cats[ci], items: cats[ci].items.filter((_, idx) => idx !== ii) };
            return { ...p, categories: cats.filter((c) => c.items.length > 0) };
        });
    };

    const updateItem = (ci, ii, field, value) => {
        setPreview((p) => {
            const cats = [...p.categories];
            const items = [...cats[ci].items];
            items[ii] = { ...items[ii], [field]: field === "price" ? parseFloat(value) || 0 : value };
            cats[ci] = { ...cats[ci], items };
            return { ...p, categories: cats };
        });
    };

    return (
        <div className="fixed inset-0 bg-black/80 flex items-start lg:items-center justify-center z-50 p-2 lg:p-4 overflow-y-auto" data-testid="ai-import-modal">
            <div className="bg-surface border border-[#1f2c44] rounded-lg p-4 lg:p-6 max-w-3xl w-full my-4">
                <div className="flex justify-between items-start mb-4 sticky top-0 bg-surface z-10">
                    <div>
                        <h2 className="font-display text-2xl font-bold flex items-center gap-2">
                            <Sparkles className="w-6 h-6 text-brand" /> Importa Menu da PDF/Foto
                        </h2>
                        <div className="text-elegant text-sm mt-1">
                            Menu: <strong className="text-warm">{menu.name}</strong> · Vision AI consuma 1 unità di quota
                        </div>
                    </div>
                    <button onClick={onClose} className="hover:text-brand"><X /></button>
                </div>

                {!preview && (
                    <>
                        <div
                            onClick={() => fileRef.current?.click()}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => { e.preventDefault(); onPick(e.dataTransfer.files?.[0]); }}
                            data-testid="ai-import-dropzone"
                            className="border-2 border-dashed border-[#1f2c44] hover:border-brand rounded-lg p-8 lg:p-12 text-center cursor-pointer transition-colors"
                        >
                            {file ? (
                                <div>
                                    <FileImage className="w-10 h-10 text-brand mx-auto mb-2" />
                                    <div className="font-medium text-warm">{file.name}</div>
                                    <div className="text-xs text-elegant mt-1">{(file.size / 1024).toFixed(0)} KB</div>
                                    <div className="text-[11px] text-brand mt-3">Clicca per cambiare</div>
                                </div>
                            ) : (
                                <div>
                                    <Upload className="w-10 h-10 text-elegant mx-auto mb-3 opacity-60" />
                                    <div className="font-medium text-warm">Trascina qui il PDF o l&apos;immagine</div>
                                    <div className="text-xs text-elegant mt-1">PDF, JPG, PNG o WebP · max 15 MB</div>
                                </div>
                            )}
                            <input ref={fileRef} type="file" accept=".pdf,image/*"
                                data-testid="ai-import-file-input"
                                className="hidden" onChange={(e) => onPick(e.target.files?.[0])} />
                        </div>

                        <div className="bg-deep border border-[#1f2c44] rounded-md p-3 mt-4 text-[11px] text-elegant leading-relaxed">
                            <strong className="text-warm">Come funziona:</strong> l&apos;AI legge il file, estrae categorie e piatti con prezzi, deduce gli allergeni dai nomi.
                            Vedrai un&apos;anteprima modificabile prima di salvare nel menu.
                            Funziona meglio con menù chiari e ben fotografati (no riflessi).
                        </div>

                        <div className="flex gap-2 mt-5">
                            <button onClick={onClose} className="flex-1 h-12 border border-[#1f2c44] rounded-md">Annulla</button>
                            <button onClick={parse} disabled={!file || parsing}
                                data-testid="ai-import-parse-button"
                                className="flex-1 h-12 bg-brand text-deep font-bold rounded-md flex items-center justify-center gap-2 disabled:opacity-40">
                                {parsing ? (
                                    <><Loader2 className="w-4 h-4 animate-spin" /> Analisi AI in corso (30-60s)...</>
                                ) : (
                                    <><Sparkles className="w-4 h-4" /> Analizza con AI</>
                                )}
                            </button>
                        </div>
                    </>
                )}

                {preview && (
                    <>
                        <div className="bg-brand/10 border border-brand/40 rounded-md p-3 mb-3 text-sm flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-brand" />
                            <span>
                                Estratti <strong>{preview.stats.items} piatti</strong> in <strong>{preview.stats.categories} categorie</strong>.
                                Rivedi e conferma per salvare.
                            </span>
                        </div>

                        <div className="bg-surface-elevated border border-[#1f2c44] rounded-md p-3 mb-3 flex items-center gap-3 flex-wrap">
                            <span className="text-xs uppercase tracking-wider text-elegant">Modalità:</span>
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                                <input type="radio" name="import-mode" value="append" checked={mode === "append"}
                                    onChange={() => setMode("append")} className="accent-brand"
                                    data-testid="ai-import-mode-append" />
                                <span>Aggiungi al menu esistente</span>
                            </label>
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                                <input type="radio" name="import-mode" value="replace" checked={mode === "replace"}
                                    onChange={() => setMode("replace")} className="accent-brand"
                                    data-testid="ai-import-mode-replace" />
                                <span className="text-amber-400">Sostituisci tutto</span>
                            </label>
                        </div>

                        <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
                            {preview.categories.map((cat, ci) => (
                                <div key={ci} className="bg-surface-elevated border border-[#1f2c44] rounded-md p-3">
                                    <div className="font-display font-bold text-brand mb-2">{cat.name}</div>
                                    <div className="space-y-1.5">
                                        {cat.items.map((it, ii) => (
                                            <div key={ii} className="flex items-center gap-2 bg-deep border border-[#1f2c44] rounded p-2"
                                                data-testid={`ai-import-item-${ci}-${ii}`}>
                                                <input type="text" value={it.name}
                                                    onChange={(e) => updateItem(ci, ii, "name", e.target.value)}
                                                    className="flex-1 bg-transparent border-0 text-sm font-medium focus:outline-none" />
                                                <input type="number" step="0.50" value={it.price}
                                                    onChange={(e) => updateItem(ci, ii, "price", e.target.value)}
                                                    className="w-20 bg-transparent border-0 text-sm text-brand text-right focus:outline-none" />
                                                <span className="text-xs text-elegant">€</span>
                                                <button onClick={() => removeItem(ci, ii)}
                                                    className="text-red-400 hover:text-red-300" title="Rimuovi">
                                                    <X className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="flex gap-2 mt-4 sticky bottom-0 bg-surface pt-2">
                            <button onClick={() => setPreview(null)} className="flex-1 h-12 border border-[#1f2c44] rounded-md">
                                Indietro
                            </button>
                            <button onClick={commit} disabled={committing || preview.categories.length === 0}
                                data-testid="ai-import-commit-button"
                                className="flex-1 h-12 bg-brand text-deep font-bold rounded-md flex items-center justify-center gap-2 disabled:opacity-40">
                                {committing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                {committing ? "Salvataggio..." : `Salva ${preview.categories.reduce((a, c) => a + c.items.length, 0)} piatti`}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
